import { FC, useCallback, useMemo, useState } from "react";
import { Command } from "@vendor/cmdk/cmdk";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    CheckboxIndicator,
    type CheckboxIndicatorState,
} from "@design-system/Checkbox/CheckboxIndicator";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { CommandsListProps, TaskEntityBulkOperation } from "./lib";
import { useCommands } from "./useCommands";
import styles from "./Commands.module.scss";

// Cycle order for a partially assigned (indeterminate) entity so the user can
// reach "add to all", "remove from all", and "leave as-is" in turn.
const INDETERMINATE_CYCLE: Record<
    CheckboxIndicatorState,
    CheckboxIndicatorState
> = {
    indeterminate: "checked",
    checked: "unchecked",
    unchecked: "indeterminate",
};

function nextCheckboxState(
    base: CheckboxIndicatorState,
    current: CheckboxIndicatorState,
): CheckboxIndicatorState {
    if (base === "indeterminate") {
        return INDETERMINATE_CYCLE[current];
    }
    return current === "checked" ? "unchecked" : "checked";
}

// Mirrors the trx bulk "multi class selection, single apply button" UX
// (see TransactionBulkClassCommandsList): toggling a row only edits local
// staged state, nothing persists until Apply fires once for the whole batch.
//
// Unlike classes, an entity can be assigned to only *some* of the eligible
// tasks (indeterminate). We track only the rows the user actually changed
// (overrides) and always derive the rendered state from the *live* option
// state, so a partially assigned entity keeps its indeterminate look and can
// be either added to every task or removed from the ones that have it.
export const TaskBulkEntityCommandsList: FC<CommandsListProps> = ({
    options = {},
}) => {
    const { close } = useCommands();
    const {
        selectedTaskCount = 0,
        taskBulkEntityOptions = [],
        taskBulkApplyEntities,
        taskBulkEntityLockedCount = 0,
    } = options;

    const baseStateById = useMemo(() => {
        const map = new Map<number, CheckboxIndicatorState>();
        for (const option of taskBulkEntityOptions) {
            map.set(option.entity.id, option.checkboxState);
        }
        return map;
    }, [taskBulkEntityOptions]);

    // Only the entities whose state the user changed; values are always the
    // resolved "checked"/"unchecked" target (never "indeterminate" — reaching
    // the untouched baseline clears the override instead).
    const [overrides, setOverrides] = useState<
        Map<number, CheckboxIndicatorState>
    >(() => new Map());

    const getDisplayState = useCallback(
        (entityId: number): CheckboxIndicatorState =>
            overrides.get(entityId) ??
            baseStateById.get(entityId) ??
            "unchecked",
        [overrides, baseStateById],
    );

    const toggleEntity = useCallback(
        (entityId: number) => {
            setOverrides((prev) => {
                const base = baseStateById.get(entityId) ?? "unchecked";
                const current = prev.get(entityId) ?? base;
                // Partially assigned rows cycle through all three states;
                // fully checked/unchecked rows just flip.
                const next = nextCheckboxState(base, current);
                const nextOverrides = new Map(prev);
                if (next === base) {
                    nextOverrides.delete(entityId);
                } else {
                    nextOverrides.set(entityId, next);
                }
                return nextOverrides;
            });
        },
        [baseStateById],
    );

    const applicableTaskCount = selectedTaskCount - taskBulkEntityLockedCount;

    const heading = useMemo(() => {
        const base = `Set entities for ${pluralizeWithCount("task", applicableTaskCount)}`;
        if (taskBulkEntityLockedCount <= 0) {
            return base;
        }
        return `${base} (${taskBulkEntityLockedCount} can't be changed — entity is locked for this task type)`;
    }, [applicableTaskCount, taskBulkEntityLockedCount]);

    const handleApply = useCallback(() => {
        const operations: TaskEntityBulkOperation[] = [];
        for (const [entityId, desired] of overrides) {
            if (desired === "checked") {
                operations.push({ entityId, operation: "add" });
            } else if (desired === "unchecked") {
                operations.push({ entityId, operation: "remove" });
            }
        }
        if (operations.length > 0) {
            taskBulkApplyEntities?.(operations);
        }
        close();
    }, [taskBulkApplyEntities, overrides, close]);

    return (
        <>
            <Command.Group heading={heading}>
                {taskBulkEntityOptions.map((option) => (
                    <Command.Item
                        key={option.entity.id}
                        value={`entity-${option.entity.id}`}
                        keywords={
                            option.entity.name ? [option.entity.name] : []
                        }
                        className={styles.commandItem}
                        onSelect={() => toggleEntity(option.entity.id)}
                        onKeyDown={(e) => {
                            if (e.key === " ") {
                                e.preventDefault();
                                toggleEntity(option.entity.id);
                            }
                        }}
                    >
                        <CheckboxIndicator
                            state={getDisplayState(option.entity.id)}
                        />
                        {option.entity.name}
                    </Command.Item>
                ))}
            </Command.Group>
            <div className={styles.classApplyFooter}>
                {overrides.size > 0 && (
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={() => setOverrides(new Map())}
                    >
                        Clear
                    </Button>
                )}
                <WithHotkey
                    hotKey="mod+enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{
                        description: "Apply entities",
                        preventDefault: true,
                        enableOnFormTags: true,
                    }}
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            disabled={overrides.size === 0}
                            onClick={handleApply}
                            rightIcon={hotkeyHint}
                        >
                            {overrides.size > 0
                                ? `Apply ${pluralizeWithCount("change", overrides.size)}`
                                : "Apply"}
                        </Button>
                    )}
                </WithHotkey>
            </div>
        </>
    );
};
